import { useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { Button, Card, Badge } from "@vlab-corporation/design-system";
import { AppLayout } from "@/components/AppLayout";
import { QueryStateHandler } from "@/components/QueryStateHandler";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { getSettlement } from "@/services/firestore/settlements";
import { getFirebaseFunctions } from "@/config/firebase";
import {
  normalizeClassFormat,
  normalizeClassType,
} from "@vlab-corporation/vlab-pilates-service-interface/branch";
import type { SettlementLineItem } from "@vlab-corporation/vlab-pilates-service-interface";
import type { AuthUser } from "@/types/auth";

const STATUS_LABEL: Record<string, string> = {
  calculating: "계산 중",
  confirmed: "확정",
  paid: "지급 완료",
};

/** 수업 형태 라벨을 결정한다. classType + isOt + memberIds 기반. */
function getSettlementClassLabel(item: SettlementLineItem): string {
  const classType =
    item.classType ?? normalizeClassFormat(item.classFormat) ?? normalizeClassType(undefined);
  const isOt = item.isOt ?? false;

  if (classType === "TRIAL_COUNSEL") return "체험단";
  if (isOt) return "OT";
  if (classType === "GROUP_REGULAR") {
    const count = item.memberIds?.length ?? 1;
    return `그룹(${count}:1)`;
  }
  return "1:1";
}

/** 회원명 표시. memberNames 배열 우선, fallback memberName, 기본 "회원". */
function getSettlementMemberLabel(item: SettlementLineItem): string {
  const names = item.memberNames;
  if (names && names.length > 1) {
    return `${names[0]} 외 ${names.length - 1}명`;
  }
  if (names && names.length === 1) return names[0];
  return item.memberName || "회원";
}

/** "YYYY-MM-DD" → "MM.DD" */
function formatClassDate(date: string): string {
  const parts = date.split("-");
  return `${parts[1]}.${parts[2]}`;
}

/** 원천세율 3.3% (사업소득세 3% + 지방소득세 0.3%) */
const WITHHOLDING_TAX_RATE = 0.033;

/** 세후 금액을 계산한다. */
function calculateAfterTax(amount: number): number {
  return amount - Math.round(amount * WITHHOLDING_TAX_RATE);
}

const STATUS_VARIANT: Record<string, "warning" | "info" | "success"> = {
  calculating: "warning",
  confirmed: "info",
  paid: "success",
};

function getCurrentYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getPrevMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  const prev = new Date(y, m - 2, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
}

function getNextMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  const next = new Date(y, m, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
}

export function SettlementPage() {
  const user = useAuthenticatedUser();
  if (!user) return null;
  return <SettlementPageContent user={user} />;
}

function SettlementPageContent({ user }: { user: AuthUser }) {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const yearMonth = searchParams.get("month") ?? getCurrentYearMonth();

  const goToPrev = useCallback(() => {
    setSearchParams({ month: getPrevMonth(yearMonth) }, { replace: true });
  }, [yearMonth, setSearchParams]);

  const goToNext = useCallback(() => {
    setSearchParams({ month: getNextMonth(yearMonth) }, { replace: true });
  }, [yearMonth, setSearchParams]);

  // 이의 신청 폼 상태
  const [isDisputeFormOpen, setIsDisputeFormOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [isSubmittingDispute, setIsSubmittingDispute] = useState(false);
  const [disputeSuccess, setDisputeSuccess] = useState(false);
  const [disputeError, setDisputeError] = useState<string | null>(null);

  const { centerOwnerUid, centerId, instructorId } = user.activeMembership;

  const {
    data: settlement,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["settlement", centerId, yearMonth],
    queryFn: () => getSettlement(centerOwnerUid, centerId, yearMonth),
  });

  const mySettlement = settlement?.instructorSettlements.find(
    (s) => s.instructorId === instructorId,
  );

  /**
   * 이의 신청 제출 핸들러
   * Cloud Function `submitSettlementDispute`를 호출하여 이의 신청을 접수한다.
   */
  const handleSubmitDispute = async () => {
    if (disputeReason.trim().length < 5) {
      setDisputeError("이의 신청 사유는 최소 5자 이상이어야 합니다.");
      return;
    }
    setIsSubmittingDispute(true);
    setDisputeError(null);
    try {
      const functions = getFirebaseFunctions();
      const callable = httpsCallable(functions, "submitSettlementDispute");
      await callable({
        ownerUid: centerOwnerUid,
        centerId,
        yearMonth,
        instructorId,
        reason: disputeReason,
      });
      setDisputeSuccess(true);
      setIsDisputeFormOpen(false);
    } catch (err: unknown) {
      setDisputeError(err instanceof Error ? err.message : "이의 신청 제출에 실패했습니다.");
    } finally {
      setIsSubmittingDispute(false);
    }
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={goToPrev}>
          ←
        </Button>
        <h2 className="text-lg font-semibold">{yearMonth}</h2>
        <Button variant="ghost" onClick={goToNext}>
          →
        </Button>
      </div>

      <QueryStateHandler
        isLoading={isLoading}
        error={error}
        errorMessage="정산 정보를 불러오지 못했습니다."
        onRetry={() => refetch()}
        isEmpty={!settlement}
        emptyMessage="해당 월의 정산 데이터가 없습니다."
      >
        {settlement && (
          <>
            {/* 정산 상태 알림 배너 */}
            {settlement.status === "confirmed" && (
              <div
                role="alert"
                className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center gap-3"
              >
                <span className="text-blue-700 font-medium">정산이 확정되었습니다.</span>
                <span className="text-blue-600 text-sm">지급 처리 대기 중입니다.</span>
              </div>
            )}
            {settlement.status === "paid" && (
              <div
                role="alert"
                className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3"
              >
                <span className="text-green-700 font-medium">정산 지급이 완료되었습니다.</span>
                <span className="text-green-600 text-sm">계좌를 확인해주세요.</span>
              </div>
            )}

            {/* 이의 신청 버튼 및 폼 */}
            {(settlement.status === "confirmed" || settlement.status === "paid") && (
              <div className="mb-4">
                {disputeSuccess ? (
                  <p className="text-green-600 text-sm">이의 신청이 접수되었습니다.</p>
                ) : !isDisputeFormOpen ? (
                  <Button variant="ghost" onClick={() => setIsDisputeFormOpen(true)}>
                    이의 신청
                  </Button>
                ) : (
                  <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                    <textarea
                      value={disputeReason}
                      onChange={(e) => setDisputeReason(e.target.value)}
                      placeholder="이의 신청 사유를 입력해주세요 (최소 5자)"
                      className="w-full border rounded p-2 text-sm"
                      rows={3}
                      aria-label="이의 신청 사유"
                    />
                    {disputeError && <p className="text-red-600 text-sm">{disputeError}</p>}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => void handleSubmitDispute()}
                        disabled={isSubmittingDispute}
                      >
                        제출
                      </Button>
                      <Button variant="ghost" onClick={() => setIsDisputeFormOpen(false)}>
                        취소
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Settlement summary */}
            <Card className="p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">정산 요약</h3>
                <Badge variant={STATUS_VARIANT[settlement.status] ?? "info"}>
                  {STATUS_LABEL[settlement.status] ?? settlement.status}
                </Badge>
              </div>
              {mySettlement ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">완료 수업</p>
                    <p className="text-2xl font-bold">{mySettlement.completedClasses}건</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">정산 금액 (VAT 포함)</p>
                    <p className="text-2xl font-bold text-primary-600">
                      ₩{mySettlement.totalAmount.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      원천세 3.3% 공제 후 ₩
                      {calculateAfterTax(mySettlement.totalAmount).toLocaleString()}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400">해당 월 정산 내역이 없습니다.</p>
              )}
            </Card>

            {/* Line items */}
            {mySettlement && mySettlement.lineItems.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium text-sm text-gray-600 mb-2">수업별 상세</h3>
                {mySettlement.lineItems.map((item) => (
                  <Card
                    key={item.classId}
                    className="p-3 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/classes/${item.classId}/journal?from=/settlement`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {item.classDate && (
                            <span className="text-xs text-gray-500">
                              {formatClassDate(item.classDate)}
                            </span>
                          )}
                          <span className="text-sm font-medium truncate">
                            {getSettlementMemberLabel(item)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge
                            variant={
                              item.memberType === null
                                ? "default"
                                : item.memberType === "firstPurchase"
                                  ? "info"
                                  : "success"
                            }
                          >
                            {item.memberType === null
                              ? "N/A"
                              : item.memberType === "firstPurchase"
                                ? "신규"
                                : "재등록"}
                          </Badge>
                          <Badge variant="info">{getSettlementClassLabel(item)}</Badge>
                          {item.isNoShow && <Badge variant="warning">노쇼</Badge>}
                        </div>
                      </div>
                      <div className="text-right ml-3">
                        <p className="font-medium">₩{item.amount.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">
                          세후 ₩{calculateAfterTax(item.amount).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-400">
                          위탁대금 ₩{item.unitPrice.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </QueryStateHandler>
    </AppLayout>
  );
}
